import { useState, useMemo, useCallback } from 'react';
import { Link } from 'wouter';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, ExternalLink, Building, Users, BarChart3, Star, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { getAllMediaOwners, type MediaOwner } from '@/data/media-owners';
import Header from '@/components/header';

export default function MediaOwners() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; // Show 12 items per page for good grid layout
  
  const mediaOwners = getAllMediaOwners();

  // Filter media owners based on search query with useMemo for performance
  const filteredMediaOwners = useMemo(() => {
    if (!searchQuery.trim()) return mediaOwners;
    
    const query = searchQuery.toLowerCase();
    return mediaOwners.filter(owner =>
      owner.name.toLowerCase().includes(query) ||
      owner.location.toLowerCase().includes(query) ||
      owner.specialties.some(specialty => 
        specialty.toLowerCase().includes(query)
      ) ||
      owner.description.toLowerCase().includes(query)
    );
  }, [mediaOwners, searchQuery]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredMediaOwners.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredMediaOwners.slice(startIndex, endIndex);

  // Reset to first page when search changes
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, []);

  // Pagination handlers
  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalPages, goToPage]);


  // Image error handler
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.src = "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=48&h=48&fit=crop";
  }, []);

  // Stats data for inventory and campaign counts
  const getOwnerStats = useCallback((ownerId: string) => {
    const stats = {
      'politis-media': { inventory: 245, campaigns: 1876 },
      'firefly-digital': { inventory: 189, campaigns: 1432 },
      'tractor-outdoor': { inventory: 156, campaigns: 987 },
      'backlite-media': { inventory: 312, campaigns: 2234 },
      'blue-label-media': { inventory: 98, campaigns: 654 },
      'qms-media': { inventory: 423, campaigns: 3456 },
      'ocean-outdoor': { inventory: 278, campaigns: 2987 },
      'eletromidia': { inventory: 134, campaigns: 876 },
      'stroer-media': { inventory: 567, campaigns: 4521 },
      'ooh-unmissable': { inventory: 234, campaigns: 1984 }
    };
    return stats[ownerId as keyof typeof stats] || { inventory: 0, campaigns: 0 };
  }, []);

  // Format follower count for mobile
  const formatFollowerCount = useCallback((count: number) => {
    return count > 1000 ? `${(count/1000).toFixed(1)}k` : count.toString();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-100">
      <Header />
      
      {/* Hero Section with Emerald Theme */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Media Owner Directory
            </h1>
            <p className="text-lg sm:text-xl text-emerald-100 mb-8 max-w-3xl mx-auto px-4">
              Discover our trusted network of premium outdoor advertising partners worldwide
            </p>
            
            {/* Search Bar with Modern Design */}
            <div className="max-w-lg mx-auto relative px-4 sm:px-0">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-emerald-600 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search by name, location, or specialty..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-12 pr-4 py-3 w-full text-base bg-white border-0 rounded-xl shadow-lg focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
                  aria-label="Search media owners"
                />
              </div>
            </div>
            
            <p className="text-sm text-emerald-200 mt-6">
              {filteredMediaOwners.length} premium partner{filteredMediaOwners.length !== 1 ? 's' : ''} available
              {totalPages > 1 && (
                <span className="ml-2">• Page {currentPage} of {totalPages}</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Media Owners Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {currentPageItems.map((owner) => {
            const stats = getOwnerStats(owner.id);
            
            return (
              <Card key={owner.id} className="group hover:shadow-2xl transition-all duration-300 hover:scale-[1.03] bg-white border-2 border-emerald-100 hover:border-emerald-300 rounded-2xl overflow-hidden">
                <CardHeader className="pb-4 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-emerald-100">
                  <div className="flex items-center space-x-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white p-2 shadow-md border-2 border-emerald-100 group-hover:border-emerald-300 transition-colors">
                        <img
                          src={owner.logo}
                          alt={`${owner.name} logo`}
                          className="w-full h-full object-contain"
                          onError={handleImageError}
                        />
                      </div>
                      {owner.verified && (
                        <div className="absolute -top-2 -right-2 bg-emerald-500 rounded-full p-1 shadow-lg">
                          <Star className="h-3 w-3 text-white fill-white" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg sm:text-xl font-bold text-emerald-800 line-clamp-2 leading-tight group-hover:text-emerald-900 transition-colors">
                        {owner.name}
                      </CardTitle>
                      <div className="flex items-center text-sm text-emerald-600 mt-2">
                        <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="truncate font-medium">{owner.location}</span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6 space-y-5">
                  <CardDescription className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
                    {owner.description}
                  </CardDescription>
                  
                  {/* Statistics with Modern Design */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
                      <Building className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                      <div className="font-bold text-lg text-emerald-800">{stats.inventory}</div>
                      <div className="text-xs text-emerald-600 font-medium">
                        <span className="hidden sm:inline">Inventory</span>
                        <span className="sm:hidden">Locations</span>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
                      <BarChart3 className="h-5 w-5 text-green-600 mx-auto mb-1" />
                      <div className="font-bold text-lg text-green-800">{stats.campaigns}</div>
                      <div className="text-xs text-green-600 font-medium">
                        <span className="hidden sm:inline">Campaigns</span>
                        <span className="sm:hidden">Projects</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Rating and Followers Row */}
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center bg-yellow-50 px-3 py-1 rounded-lg">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 mr-1" />
                      <span className="font-bold text-yellow-800">{owner.rating}</span>
                      <span className="text-xs text-yellow-600 ml-1 hidden sm:inline">
                        ({owner.reviewCount.toLocaleString()})
                      </span>
                    </div>
                    <div className="flex items-center text-emerald-700">
                      <Users className="h-4 w-4 mr-1" />
                      <span className="font-semibold text-sm">
                        {formatFollowerCount(owner.followers)}
                        <span className="hidden sm:inline"> followers</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Specialties with Emerald Theme */}
                  <div className="flex flex-wrap gap-2">
                    {owner.specialties.slice(0, 2).map((specialty, index) => (
                      <Badge 
                        key={index} 
                        className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200 text-xs px-3 py-1 font-medium"
                      >
                        {specialty}
                      </Badge>
                    ))}
                    {owner.specialties.length > 2 && (
                      <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs px-2 py-1">
                        +{owner.specialties.length - 2}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Visit Store Button with Brand Color */}
                  <Link href={`/store/${owner.id}`} className="block">
                    <Button 
                      className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                      onClick={() => {
                        // Set back URL context for media owner directory
                        sessionStorage.setItem('mediaOwnerBackUrl', '/media-owners');
                        console.log('Media owner directory: Setting back URL to /media-owners');
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visit Store
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {filteredMediaOwners.length === 0 && (
          <div className="col-span-full text-center py-16 px-4">
            <div className="bg-white rounded-3xl p-12 shadow-xl border-2 border-emerald-100 max-w-md mx-auto">
              <Building className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
              <h3 className="text-xl font-bold text-emerald-800 mb-3">
                No partners found
              </h3>
              <p className="text-emerald-600 leading-relaxed">
                Try adjusting your search terms or browse all available media partners.
              </p>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="col-span-full flex justify-center items-center mt-12 space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className="flex items-center space-x-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </Button>

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(pageNum)}
                    className={
                      currentPage === pageNum
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    }
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center space-x-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}